import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, ConfigProvider, Input, Modal, Typography } from 'antd';

import AdHocInputEditor, { adHocInputProblem } from '../common/AdHocInputEditor';
import CardActionPreview from './CardActionPreview';
import CardActionSteps from './CardActionSteps';
import ConditionBuilder, { buildConditionFields } from '../common/ConditionBuilder';
import QueryRefusalPick from '../items/QueryRefusalPick';
import { InputIconSelect, InputSelect } from '../Input';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { PLUS } from '../icons';
import { createAutomation, patchAutomation } from '../../services/automations';
import useElementChoices from '../../hooks/useElementChoices';
import { idsNamedIn } from '../../utils/filterIds';
import { normalizeId } from '../../utils/ids';
import {
	MAX_CONFIRM,
	MAX_INPUTS,
	MAX_LABEL,
	MAX_REASON,
	blankDraft,
	buildPayload,
	draftFromRule,
	goneElementSentence,
	inputIsRead,
	newInput,
	refusalTarget,
	withKeyFollowingLabel,
} from '../../utils/cardActionEditor';

const { Text } = Typography;

/** The API's sentence for a field, drawn under it and announced. */
function FieldError({ id, children }) {
	if (!children) return null;
	return <Text id={id} type="danger" role="alert" className="d-block card-action-editor__error">{children}</Text>;
}

/**
 * Create or edit one card action (`kind: 'card-action'`, keepr-api KPR-382):
 * a named button a manager defines on a card, pressed by anyone who can edit
 * the record. Its own modal beside the other automation editors (the reminder,
 * the generator, the threshold), for the same reason they have theirs: what a
 * button asks is not what a rule asks.
 *
 *   Name and card     the button appears on this card and the cards beneath it
 *   Button            label, icon, and an optional question asked first
 *   When it applies   a condition over the record's own values (the condition
 *                     builder; its text is one click away) and the sentence a
 *                     person reads where the button does not apply
 *   Ask for           the small form a press opens (the multi add form's input
 *                     editor): label, type, required, default
 *   Then              the steps: set values on the item, or create a related
 *                     item linked back to it. Values come from pills, never
 *                     typed templates; a step set up elsewhere is kept.
 *   Preview           what a press would write for a chosen record
 *
 * Enable, disable and "waiting for you" live on the Automations tab's row. A
 * schedule joins this editor with the scheduled kind (KPR-549).
 *
 * The API validates the whole action at write time, so a refusal arrives in
 * words written for a person: it is drawn UNDER the field it is about
 * (`refusalTarget`), verbatim, never paraphrased, and a message with no field
 * is shown at the foot.
 */
export default function CardActionEditor({
	open,
	collectionId,
	rule,
	collectionCards = [],
	cardsById = {},
	getElementsForCard,
	onClose,
	onSaved,
}) {
	const { message } = App.useApp();
	const [draft, setDraft] = useState(blankDraft);
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState({});
	const [conditionCleared, setConditionCleared] = useState(false);
	const isNew = !rule?._id;
	// The editor's selects and menus open inside it, so its touch-size rules reach them.
	const bodyRef = useRef(null);
	const popupContainer = useCallback(() => bodyRef.current || document.body, []);

	// Seeding happens on OPEN, never straight off the prop: Cancel discards edits.
	useEffect(() => {
		if (!open) return;
		setDraft(rule ? draftFromRule(rule) : blankDraft());
		setErrors({});
		setConditionCleared(false);
	}, [open, rule]);

	const cardOptions = useMemo(() => buildCardHierarchyOptions(collectionCards), [collectionCards]);
	const elementsFor = useCallback(
		cardId => (cardId && getElementsForCard ? (getElementsForCard(cardId) || []) : []),
		[getElementsForCard]
	);
	const cardName = cardsById?.[draft.cardId]?.name || '';
	const cardElements = useMemo(() => elementsFor(draft.cardId), [elementsFor, draft.cardId]);

	// A condition names elements by id; one the card has since lost is said.
	const gone = useMemo(() => {
		if (!draft.kql.trim() || !cardElements.length) return [];
		return idsNamedIn(draft.kql, buildConditionFields(cardElements)).filter(entry => entry.noun === 'element' && entry.removed);
	}, [draft.kql, cardElements]);

	const picks = useElementChoices(JSON.stringify([draft.cardId, draft.kql]));
	const resetPicks = picks.reset;
	useEffect(() => { if (open) resetPicks(); }, [open, resetPicks]);

	const patch = change => setDraft(prev => ({ ...prev, ...change }));
	const fail = target => {
		if (!target) return;
		setErrors(prev => {
			const next = { ...prev };
			if (target.where === 'step') next.step = { ...(prev.step || {}), [target.index]: target.message };
			else if (target.where === 'input') next.input = { ...(prev.input || {}), [target.key]: target.message };
			else next[target.where] = target.message;
			return next;
		});
	};

	const changeCard = cardId => {
		const moved = Boolean(draft.cardId) && draft.cardId !== cardId;
		// A condition is about one card's elements: another card cannot hold it.
		if (moved && draft.kql.trim()) setConditionCleared(true);
		setDraft(prev => ({ ...prev, cardId, ...(moved ? { kql: '' } : {}) }));
	};

	const changeInput = (index, next) => setDraft(prev => {
		const before = prev.inputs[index];
		const settled = withKeyFollowingLabel(before, next, { steps: prev.steps, taken: new Set(prev.inputs.map(input => input.key)) });
		return { ...prev, inputs: prev.inputs.map((input, i) => (i === index ? settled : input)) };
	});
	const removeInput = index => {
		const input = draft.inputs[index];
		if (inputIsRead(draft.steps, input.key)) {
			fail({ where: 'input', key: input.key, message: 'A step uses this input. Take it out of the step first, then remove the input.' });
			return;
		}
		patch({ inputs: draft.inputs.filter((_, i) => i !== index) });
	};
	const addInput = () => patch({ inputs: [...draft.inputs, newInput(new Set(draft.inputs.map(input => input.key)))] });

	const save = async () => {
		if (saving) return;
		setErrors({});
		for (const input of draft.inputs) {
			const problem = adHocInputProblem(input);
			if (problem) { fail({ where: 'input', key: input.key, message: problem }); return; }
		}
		const payload = buildPayload(draft, { isNew });
		setSaving(true);
		try {
			if (isNew) await createAutomation(collectionId, picks.withChoices(payload));
			else await patchAutomation(collectionId, normalizeId(rule._id), picks.withChoices(payload));
			picks.reset();
			message.success(isNew ? 'Action created.' : 'Action saved.');
			onSaved?.();
		} catch (err) {
			// An element the condition could mean several of is asked under the
			// condition (QueryRefusalPick draws it); every other refusal is placed.
			if (!picks.take(err)) {
				if (err?.response?.data) fail(refusalTarget(err.response.data));
				else fail({ where: 'general', message: 'The action could not be saved. Check your connection and try again.' });
			}
		}
		setSaving(false);
	};

	// The unsaved action as a create takes it, which is what the preview asks for.
	const previewBody = () => buildPayload(draft, { isNew: true });

	const context = { collectionId, cardId: draft.cardId, cardsById, getElementsForCard, cardOptions, inputs: draft.inputs };
	const stepErrors = errors.step || {};
	const waiting = rule?.awaitingPerson && rule?.enabled === false;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={save}
			okText="Save"
			confirmLoading={saving}
			title={isNew ? 'New action' : 'Edit action'}
			width={760}
			destroyOnClose
			className="card-action-editor"
		>
			<ConfigProvider getPopupContainer={popupContainer}>
				<div className="card-action-editor__body" ref={bodyRef}>
					{waiting ? (
						<Alert
							type="info"
							showIcon
							message="This action is waiting for you"
							description="An assistant or a setup made it. Look over the steps, then turn it on from the list: until then the button does not appear."
						/>
					) : null}

					<section className="card-action-editor__section">
						<label htmlFor="card-action-name" className="d-block">Name</label>
						<Input
							id="card-action-name"
							size="large"
							value={draft.name}
							status={errors.name ? 'error' : undefined}
							placeholder="What this button does, in a few words"
							onChange={event => patch({ name: event.target.value })}
						/>
						<FieldError>{errors.name}</FieldError>
						<InputSelect
							elementName="cardActionCard"
							elementLabel="Card"
							value={draft.cardId || undefined}
							onChange={(n, value) => changeCard(value || '')}
							options={cardOptions}
							placeholder="Which card gets the button?"
							allowClear={false}
						/>
						<Text type="secondary" className="d-block">The button appears on items of this card and of the cards beneath it.</Text>
						<FieldError>{errors.card}</FieldError>
					</section>

					<section className="card-action-editor__section">
						<Text strong className="d-block">Button</Text>
						<div className="card-action-editor__pair">
							<div>
								<label htmlFor="card-action-label" className="d-block">Label</label>
								<Input
									id="card-action-label"
									value={draft.label}
									maxLength={MAX_LABEL}
									status={errors.label ? 'error' : undefined}
									placeholder={draft.name.trim() || 'The words on the button'}
									onChange={event => patch({ label: event.target.value })}
								/>
								<FieldError>{errors.label}</FieldError>
							</div>
							<div>
								<InputIconSelect
									elementName="cardActionIcon"
									elementLabel="Icon"
									value={draft.icon || undefined}
									onChange={(n, value) => patch({ icon: value || '' })}
								/>
								<FieldError>{errors.icon}</FieldError>
							</div>
						</div>
						<label htmlFor="card-action-confirm" className="d-block">Ask before it runs</label>
						<Input.TextArea
							id="card-action-confirm"
							rows={2}
							value={draft.confirm}
							maxLength={MAX_CONFIRM}
							showCount
							status={errors.confirm ? 'error' : undefined}
							placeholder="A question to confirm, if the person should be sure"
							onChange={event => patch({ confirm: event.target.value })}
						/>
						<FieldError>{errors.confirm}</FieldError>
					</section>

					<section className="card-action-editor__section">
						<Text strong className="d-block">When it applies</Text>
						{conditionCleared ? (
							<Alert type="info" showIcon message="The condition was cleared: it was about the previous card's elements." />
						) : null}
						<ConditionBuilder
							value={draft.kql}
							onChange={kql => { patch({ kql }); setConditionCleared(false); }}
							elements={cardElements}
							collectionId={collectionId}
							emptyText="No condition: the button is available on every item of this card."
						/>
						{gone.length ? (
							<Alert type="warning" showIcon className="card-action-editor__gone" message={goneElementSentence(gone, cardName)} />
						) : null}
						<FieldError>{errors.condition}</FieldError>
						<QueryRefusalPick
							inline
							refusal={picks.refusal}
							disabled={saving}
							onPick={value => { picks.pick(value); save(); }}
							onRepick={() => { picks.clearRefusal(); save(); }}
						/>
						<label htmlFor="card-action-reason" className="d-block">What people read where it does not apply</label>
						<Input
							id="card-action-reason"
							value={draft.reason}
							maxLength={MAX_REASON}
							showCount
							status={errors.reason ? 'error' : undefined}
							placeholder="Optional. Without one, keepr words the condition"
							onChange={event => patch({ reason: event.target.value })}
						/>
						<FieldError>{errors.reason}</FieldError>
						<Text type="secondary" className="d-block">The button is listed in the item menu on every item, and shown on the item page only where it applies.</Text>
					</section>

					<section className="card-action-editor__section">
						<div className="card-action-editor__head">
							<Text strong>Ask for</Text>
							<Button size="small" icon={<PLUS />} onClick={addInput} disabled={draft.inputs.length >= MAX_INPUTS}>Add input</Button>
						</div>
						{draft.inputs.length ? null : <Text type="secondary" className="d-block">Nothing is asked: the button runs when it is pressed.</Text>}
						<FieldError>{errors.inputs}</FieldError>
						{draft.inputs.map((input, index) => (
							<div key={input.uid}>
								<AdHocInputEditor
									input={input}
									index={index}
									testId={`card-action-input-${index}`}
									dead={Boolean(errors.input?.[input.key])}
									cardOptions={cardOptions}
									onChange={next => changeInput(index, next)}
									onRemove={() => removeInput(index)}
								/>
								<FieldError>{errors.input?.[input.key]}</FieldError>
							</div>
						))}
						{draft.inputs.length ? <Text type="secondary" className="d-block">What is asked is offered in the {'{{ }}'} menu of each value below.</Text> : null}
					</section>

					<section className="card-action-editor__section">
						<Text strong className="d-block">Then</Text>
						<FieldError>{errors.steps}</FieldError>
						<CardActionSteps
							steps={draft.steps}
							onChange={steps => patch({ steps })}
							context={context}
							errors={stepErrors}
						/>
					</section>

					<section className="card-action-editor__section">
						<Text strong className="d-block">Preview</Text>
						<CardActionPreview
							collectionId={collectionId}
							cardId={draft.cardId}
							cardName={cardName}
							steps={draft.steps}
							inputs={draft.inputs}
							buildBody={previewBody}
							elementsFor={elementsFor}
							onRefusal={fail}
						/>
					</section>

					<FieldError>{errors.general}</FieldError>
				</div>
			</ConfigProvider>
		</Modal>
	);
}
